import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  FormProps,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Switch,
} from "antd";
import { FC, useEffect } from "react";
import { StarRating } from "~/components/common/star-rating";
import { useGetStaffRoles } from "~/services/common";
import {
  CreateStaffParams,
  OPERATION_KEYS,
  useCreateStaff,
  useGetStaffById,
  useUpdateStaff,
} from "~/services/operation";
import { capitalize } from "~/utils";
import { Role, StaffStatus } from "~/utils/constants";
import { useStaffStore } from "../staffs.store";

// Labels for staff status dropdown (excluding ON_ASSIGNMENT which is deprecated)
const staffStatusOptions: { value: StaffStatus; label: string }[] = [
  { value: StaffStatus.ACTIVE, label: "Active" },
  { value: StaffStatus.HIATUS, label: "Hiatus" },
  { value: StaffStatus.INACTIVE, label: "Inactive" },
];
const skills = [
  "installation",
  "packing",
  "handling",
  "clientRelations",
  "teamwork",
];

type Props = {};

export const ModalStaff: FC<Props> = () => {
  const qc = useQueryClient();

  const { formState, closeStaffModal } = useStaffStore();

  const [form] = Form.useForm<CreateStaffParams>();

  const { data: staff, isLoading: loadingStaff } = useGetStaffById(
    formState.staffId
  );
  const { data: staffRoles, isLoading: loadingStaffRoles } = useGetStaffRoles();

  useEffect(() => {
    if (!staff || !staff.user) return;

    form.setFieldsValue({
      username: staff.user.username,
      email: staff.user.email,
      firstName: staff.user.firstName,
      lastName: staff.user.lastName,
      phoneNumber: staff.user.phoneNumber,
      staff: {
        staffRoleId: staff.staffRole?.id,
        clientRelations: staff.clientRelations,
        installation: staff.installation,
        packing: staff.packing,
        handling: staff.handling,
        teamwork: staff.teamwork,
        lead: staff.lead,
        driver: staff.driver,
        position: staff.position ?? "",
        skills: staff.skills ?? [],
        status: staff.status ?? StaffStatus.ACTIVE,
      },
    });
  }, [staff, form]);

  const mutation = useCreateStaff();

  const updateMutation = useUpdateStaff();

  const onFinish: FormProps<CreateStaffParams>["onFinish"] = (values) => {
    const onSuccess = () => {
      qc.invalidateQueries({
        queryKey: [OPERATION_KEYS.STAFF],
      });
      closeStaffModal();
    };
    if (!staff) {
      mutation.mutate(
        {
          ...values,
          username: values.username.trim(),
          role: Role.STAFF,
          password: "password123",
        },
        {
          onSuccess,
        }
      );
    } else {
      updateMutation.mutate(
        {
          ...values,
          id: staff.user.id as string,
        },
        {
          onSuccess,
        }
      );
    }
  };

  return (
    <Modal
      destroyOnHidden
      open={formState.open}
      onCancel={() => {
        closeStaffModal();
        form.resetFields();
      }}
      footer={null}
      className="w-5xl!"
      title={
        <div className="flex flex-col justify-center items-center gap-1">
          <span className="text-2xl font-semibold">
            {!staff ? "Create New Staff" : `Edit ${staff.user.username}`}
          </span>
          {!staff && (
            <span className="text-sm text-gray-500 ml-2">
              Fill in the details below
            </span>
          )}
        </div>
      }
    >
      <Spin spinning={loadingStaff || loadingStaffRoles}>
        <Form form={form} size="large" layout="vertical" onFinish={onFinish}>
          <Row gutter={16} className="mb-4">
            <Col span={16}>
              <Card title="Staff Information">
                <Form.Item
                  label="Username"
                  name="username"
                  rules={[
                    { required: true, message: "Please input username!" },
                  ]}
                >
                  <Input placeholder="Enter username" />
                </Form.Item>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[{ required: true, message: "Please input email!" }]}
                >
                  <Input type="email" placeholder="Enter email" />
                </Form.Item>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="First Name"
                      name="firstName"
                      rules={[
                        { required: true, message: "Please enter first name!" },
                      ]}
                    >
                      <Input placeholder="Enter first name" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Last Name"
                      name="lastName"
                      rules={[
                        { required: true, message: "Please enter last name!" },
                      ]}
                    >
                      <Input placeholder="Enter last name" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Phone"
                  name="phoneNumber"
                  rules={[
                    { required: true, message: "Please input phone number!" },
                  ]}
                >
                  <Input type="phone" placeholder="Enter phone number" />
                </Form.Item>

                <Form.Item
                  label="Staff Role"
                  name={["staff", "staffRoleId"]}
                  rules={[
                    { required: true, message: "Please select staff role!" },
                  ]}
                >
                  <Select
                    placeholder="Select role"
                    loading={loadingStaffRoles}
                    options={staffRoles?.items.map((role) => ({
                      label: role.name,
                      value: role.id,
                    }))}
                  />
                </Form.Item>
                <Form.Item
                  label="Position"
                  name={["staff", "position"]}
                  rules={[{ required: false }]}
                >
                  <Input placeholder="Enter primary position" />
                </Form.Item>

                <Form.Item
                  label="Skills & Certifications"
                  name={["staff", "skills"]}
                  rules={[{ required: false }]}
                  help="Enter skills and certifications separated by commas (e.g., Forklift Certified, Art Handling, Installation)"
                >
                  <Select
                    mode="tags"
                    placeholder="Enter skills and certifications"
                    style={{ width: '100%' }}
                    tokenSeparators={[',']}
                    maxTagCount="responsive"
                  />
                </Form.Item>

                <div className="flex items-center gap-4">
                  <Form.Item
                    label="Is Lead"
                    name={["staff", "lead"]}
                    valuePropName="checked"
                    layout="horizontal"
                    className="mb-0"
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item
                    label="Is Driver"
                    name={["staff", "driver"]}
                    valuePropName="checked"
                    layout="horizontal"
                    className="mb-0"
                  >
                    <Switch />
                  </Form.Item>
                </div>

                {/* Status dropdown - only show when editing existing staff */}
                {staff && (
                  <Form.Item
                    label="Status"
                    name={["staff", "status"]}
                    className="mt-4"
                  >
                    <Select
                      placeholder="Select status"
                      options={staffStatusOptions}
                    />
                  </Form.Item>
                )}
              </Card>
            </Col>
            <Col span={8}>
              <Card title="Staff Skills">
                {skills.map((skill) => (
                  <div
                    className="grid grid-cols-18 gap-4 items-center"
                    key={skill}
                  >
                    <div className="col-span-8 flex items-center">
                      <span className="font-bold">{capitalize(skill)}</span>
                    </div>
                    <div className="col-span-10 flex items-center">
                      <Form.Item
                        name={["staff", skill]}
                        key={skill}
                        valuePropName="rating"
                        rules={[
                          {
                            required: true,
                            message: "Please rate this skill!",
                          },
                        ]}
                        className="mb-0"
                      >
                        <StarRating interactive />
                      </Form.Item>
                    </div>
                  </div>
                ))}
                <Divider />
                <Form.Item shouldUpdate>
                  {({ getFieldValue }) => {
                    const installation =
                      getFieldValue(["staff", "installation"]) ?? 0;
                    const packing = getFieldValue(["staff", "packing"]) ?? 0;
                    const handling = getFieldValue(["staff", "handling"]) ?? 0;
                    const clientRelations =
                      getFieldValue(["staff", "clientRelations"]) ?? 0;
                    const teamwork = getFieldValue(["staff", "teamwork"]) ?? 0;
                    const rating =
                      (installation +
                        packing +
                        handling +
                        clientRelations +
                        teamwork) /
                      5;
                    return (
                      <div className="grid grid-cols-16 gap-4 items-center">
                        <div className="col-span-6 flex items-center">
                          <span className="font-bold">Overall ({rating})</span>
                        </div>
                        <div className="col-span-10 flex items-center">
                          <Form.Item className="mb-0">
                            <StarRating rating={rating} />
                          </Form.Item>
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <Button
            type="primary"
            htmlType="submit"
            className="w-full"
            size="large"
            loading={mutation.isPending || updateMutation.isPending}
          >
            {!staff ? "Create" : "Edit"} Staff
          </Button>
        </Form>
      </Spin>
    </Modal>
  );
};
